// Exports: SubtaskRow
import React, { memo, useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { CgPushRight } from "react-icons/cg";
import Xarrow from "react-xarrows";
import CalendarModal from "screens/Project Dashboard/Tasks/CalendarModal";
import {
  mapBackground,
  mapDurationTextClass,
  renderSubtaskDays,
  renderSubtaskTextForDue,
} from "utils/tasks/taskDateUtils";
import AddSubtaskMemberModal from "./AddSubtaskMemberModal";
import StatusBadge from "./StatusBadge";

const SubtaskRowComponent = ({
  tg,
  task,
  st,
  taskAuthority,
  editingSubId,
  onSubEditStart,
  onSubEditCancel,
  onSubEditSubmit,
  handleSubtaskDateChange,
  onToggleSubComplete,
  setSelectedTaskGroup,
  setSelectedTask,
  setSelectedSubtask,
  getInitials,
  preserveScroll,
  refetchSubtasks,
  mergeGroupAllocatedMembers,
  closeAddForms,
  canReorderSubtask,
  isDragging,
  dropIndicatorPosition,
  onReorderDragStart,
  onReorderDragEnd,
  onReorderDragOver,
  onReorderDrop,
}) => {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const pendingSubtaskNameRef = useRef(st.name);
  const inputRef = useRef(null);
  const rowDropShadow =
    dropIndicatorPosition === "before"
      ? "inset 0 2px 0 0 #3B0069"
      : dropIndicatorPosition === "after"
        ? "inset 0 -2px 0 0 #3B0069"
        : undefined;

  useEffect(() => {
    if (editingSubId !== st.id) return;
    pendingSubtaskNameRef.current = st.name;
    if (inputRef.current) {
      inputRef.current.value = st.name;
    }
  }, [editingSubId, st.id, st.name]);

  const openSubtaskDetails = () => {
    setSelectedSubtask({ ...st, task_id: task.id });
    setSelectedTaskGroup(null);
    setSelectedTask(null);
  };

  const submitSubtaskName = () =>
    preserveScroll(`[data-scroll-anchor="subtask-${st.id}"]`, () =>
      onSubEditSubmit(pendingSubtaskNameRef.current),
    );

  return (
    <>
      <Xarrow
        key={`task-${task.id}-${task.torder_index ?? "na"}-subtask-${st.id}-${st.subtask_order_index ?? "na"}`}
        start={`task-${task.id}`}
        end={`subtask-${st.id}`}
        startAnchor="bottom"
        endAnchor="left"
        strokeWidth={2}
        color="#3B0069"
        zIndex={0}
        path="grid"
      />

      <tr
        className={clsx(
          "py-1 group hover:bg-[#ECE9EE] cursor-pointer col-span-8",
          {
            "opacity-60": isDragging,
          },
        )}
        style={{
          borderBottom: "1px solid #78797A",
          boxShadow: rowDropShadow,
        }}
        data-scroll-anchor={`subtask-${st.id}`}
        id={`subtask-${st.id}`}
        onDragOver={(event) => onReorderDragOver?.(event, st.id)}
        onDrop={(event) => onReorderDrop?.(event, st.id)}
      >
        <td
          className="pl-3 xl:pl-3 pr-1 py-1 xl:py-2 text-left w-[160px] xl:w-[200px] text-xs xl:text-sm text-gray-700"
          onClick={(event) => {
            event.stopPropagation();
            openSubtaskDetails();
          }}
        >
          <div className="flex flex-row items-center justify-between w-full">
            <div className="flex items-center w-[85%]">
              <button
                type="button"
                draggable={canReorderSubtask}
                aria-label={`Reorder ${st.name}`}
                title={canReorderSubtask ? "Drag to reorder" : undefined}
                className={clsx("mr-1 rounded p-[1px] text-gray-400", {
                  "cursor-grab hover:bg-gray-200 hover:text-gray-600 active:cursor-grabbing":
                    canReorderSubtask,
                  "opacity-0 pointer-events-none": !canReorderSubtask,
                })}
                onClick={(event) => event.stopPropagation()}
                onDragStart={(event) => onReorderDragStart?.(event, st.id)}
                onDragEnd={onReorderDragEnd}
              >
                <svg
                  viewBox="0 0 16 16"
                  className="h-4 w-4 xl:h-[18px] xl:w-[18px]"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <circle cx="5" cy="4" r="1.2" />
                  <circle cx="11" cy="4" r="1.2" />
                  <circle cx="5" cy="8" r="1.2" />
                  <circle cx="11" cy="8" r="1.2" />
                  <circle cx="5" cy="12" r="1.2" />
                  <circle cx="11" cy="12" r="1.2" />
                </svg>
              </button>

              <input
                type="checkbox"
                className="mr-2 text-purple-600"
                style={{
                  accentColor: "#3B0069",
                }}
                checked={st.status === "Completed"}
                height={20}
                width={20}
                // disabled={true}
                // onClick={(event) => event.stopPropagation()}
                // onChange={(event) => {
                //   preserveScroll(
                //     `[data-scroll-anchor="subtask-${st.id}"]`,
                //     () =>
                //       onToggleSubComplete(
                //         tg.id,
                //         task.id,
                //         st.id,
                //         event.target.checked,
                //       ),
                //   );
                // }}
              />

              {editingSubId === st.id ? (
                <form
                  onSubmit={(event) => {
                    event.preventDefault();
                    submitSubtaskName();
                  }}
                  className="flex items-center"
                  onClick={(event) => event.stopPropagation()}
                >
                  <input
                    autoFocus
                    ref={inputRef}
                    defaultValue={st.name}
                    maxLength={100}
                    onChange={(event) => {
                      pendingSubtaskNameRef.current = event.target.value;
                    }}
                    onBlur={(event) => {
                      event.preventDefault();
                      submitSubtaskName();
                    }}
                    className="pl-2 w-full text-[14px] mr-2 border border-purple-300 rounded"
                  />
                  <button
                    type="button"
                    onClick={onSubEditCancel}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ✕
                  </button>
                </form>
              ) : (
                <span
                  className={`truncate cursor-pointer text-wrap flex px-1 rounded-lg flex-wrap hover:text-purple-600 ${
                    taskAuthority
                      ? "hover:outline hover:outline-1 hover:outline-black"
                      : ""
                  }`}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (taskAuthority) {
                      closeAddForms();
                      preserveScroll(
                        `[data-scroll-anchor="subtask-${st.id}"]`,
                        () => onSubEditStart(st),
                      );
                    } else {
                      openSubtaskDetails();
                    }
                  }}
                >
                  {st.name}
                </span>
              )}
            </div>

            <button
              className="ml-2 p-0.5 hover:bg-gray-200 rounded-full text-gray-600"
              onClick={(event) => {
                event.stopPropagation();
                openSubtaskDetails();
              }}
              title="Details"
            >
              <CgPushRight
                size={18}
                color="black"
                className="hidden group-hover:block"
              />
            </button>
          </div>
        </td>

        <td className="pl-2 xl:pl-3 pr-1 py-1 xl:py-2 text-center w-[130px] xl:w-[170px]">
          <div
            className="flex flex-row items-center justify-start w-full px-2 xl:px-3"
            onClick={() => {
              preserveScroll(`[data-scroll-anchor="subtask-${st.id}"]`, () =>
                setShowMemberModal((current) => !current),
              );
            }}
          >
            {Array.isArray(st.assignee) && st.assignee.length ? (
              <>
                {st.assignee.slice(0, 3).map((member) =>
                  member?.userpic ? (
                    <img
                      key={member.member_id}
                      src={member.userpic}
                      alt={`${member.first_name} ${member.last_name}`}
                      className="w-6 h-6 xl:w-8 xl:h-8 rounded-full border-2 border-white"
                      style={{
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <span
                      key={member.member_id}
                      className="flex flex-row items-center justify-center w-6 h-6 xl:w-8 xl:h-8 bg-[#343C6A] rounded-full text-white font-medium text-[10px] xl:text-sm border-2 border-white"
                      title={`${member.first_name} ${member.last_name}`}
                    >
                      {getInitials(`${member.first_name} ${member.last_name}`)}
                    </span>
                  ),
                )}
                {st.assignee.length > 3 && (
                  <span
                    className="flex flex-row items-center justify-center w-6 h-6 xl:w-8 xl:h-8 rounded-full bg-[#343C6A] text-white font-medium text-[10px] xl:text-sm border-2 border-white"
                    title={`+${st.assignee.length - 3} more members`}
                  >
                    +{st.assignee.length - 3}
                  </span>
                )}
              </>
            ) : (
              <span className="text-gray-500">Unassigned</span>
            )}
          </div>

          {showMemberModal && (
            <AddSubtaskMemberModal
              task={task}
              subtask={st}
              getInitials={getInitials}
              preserveScroll={preserveScroll}
              onMembersUpdated={async (members) => {
                await refetchSubtasks(task.task_group, task.id);
                mergeGroupAllocatedMembers?.(task.task_group, members);
              }}
              onClose={() => setShowMemberModal(false)}
            />
          )}
        </td>

        <td
          className="px-1 py-1 xl:py-2 text-center w-[100px] xl:w-[130px] relative"
          onClick={(event) => {
            event.stopPropagation();
            preserveScroll(`[data-scroll-anchor="subtask-${st.id}"]`, () =>
              setIsCalendarOpen(true),
            );
          }}
        >
          {st.dueDate ? (
            st.dueDate
          ) : (
            <div className="flex flex-row items-center justify-center w-full">
              <span className="text-[10px] xl:text-sm bg-purple-100 text-purple-700 px-1 xl:px-2 py-1 rounded-md hover:bg-purple-200">
                Select date
              </span>
            </div>
          )}
          <CalendarModal
            isVisible={isCalendarOpen}
            startDate={st.start_date}
            endDate={st.end_date}
            onClose={() => setIsCalendarOpen(false)}
            onDateChange={(startDate, endDate) => {
              preserveScroll(`[data-scroll-anchor="subtask-${st.id}"]`, () => {
                const result = handleSubtaskDateChange(
                  st.id,
                  startDate,
                  endDate,
                );
                setIsCalendarOpen(false);
                return result;
              });
            }}
          />
        </td>

        <td className="px-1 xl:px-2 py-1 xl:py-2 text-center w-[100px] xl:w-[130px]">
          <div className="w-full px-1 xl:px-2 mx-auto">
            {st.dueDate && <StatusBadge value={st.status} />}
          </div>
        </td>

        <td className="px-2 xl:px-4 py-1 xl:py-2 text-center w-[80px] xl:w-[110px]">
          {st.is_completed && (
            <div className="flex flex-col items-center justify-center w-full px-1 xl:px-2">
              <span className="text-[10px] xl:text-sm font-normal">
                Completed on
              </span>
              <span className="text-[9px] xl:text-xs font-medium">
                {st.completed_on || st.end_date}
              </span>
            </div>
          )}
        </td>

        <td className="px-1 xl:px-2 py-1 xl:py-2 text-center w-[100px] xl:w-[130px]">
          {st.dueDate && (
            <div
              className={clsx(
                "rounded-md px-1 xl:px-3 py-1 justify-end flex flex-row items-center font-medium text-[10px] xl:text-xs",
                mapDurationTextClass(st),
              )}
              style={{
                gap: "5px",
                background: mapBackground(st),
              }}
            >
              {renderSubtaskTextForDue(st)}
              <div className="rounded-full bg-white p-0.5 xl:p-1 text-[#343C6A] font-semibold">
                {renderSubtaskDays(st)}D
              </div>
            </div>
          )}
        </td>
      </tr>
    </>
  );
};

const areEqual = (previousProps, nextProps) => {
  const subtaskEditAffected =
    previousProps.editingSubId === previousProps.st.id ||
    nextProps.editingSubId === nextProps.st.id;

  return (
    previousProps.tg === nextProps.tg &&
    previousProps.task === nextProps.task &&
    previousProps.st === nextProps.st &&
    previousProps.taskAuthority === nextProps.taskAuthority &&
    previousProps.canReorderSubtask === nextProps.canReorderSubtask &&
    previousProps.isDragging === nextProps.isDragging &&
    previousProps.dropIndicatorPosition === nextProps.dropIndicatorPosition &&
    (!subtaskEditAffected ||
      previousProps.editingSubId === nextProps.editingSubId)
  );
};

const SubtaskRow = memo(SubtaskRowComponent, areEqual);

export default SubtaskRow;
